import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCompetencyExam } from '../hooks/useCompetency';

const CompetencyExam = () => {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const { exam, startExam, submitExam, getResults, submitting, error } = useCompetencyExam(parseInt(enrollmentId));

  const [examStarted, setExamStarted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [examResults, setExamResults] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Timer countdown
  useEffect(() => {
    if (!examStarted || !exam || timeRemaining === null) return;

    if (timeRemaining <= 0) {
      handleSubmit();
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [examStarted, exam, timeRemaining]);

  const handleStartExam = async () => {
    try {
      const examData = await startExam();
      setExamStarted(true);

      // Set initial time remaining (convert minutes to seconds)
      const durationMinutes = examData.duration_minutes || 90;
      setTimeRemaining(durationMinutes * 60);

      // Initialize answers object
      const initialAnswers = {};
      examData.selected_questions.forEach((q, idx) => {
        initialAnswers[idx] = '';
      });
      setAnswers(initialAnswers);
    } catch (err) {
      alert('Failed to start exam: ' + err.message);
    }
  };

  const handleAnswerChange = (questionIndex, value) => {
    setAnswers({
      ...answers,
      [questionIndex]: value
    });
  };

  const handleSubmit = async () => {
    if (!exam) return;

    const confirmSubmit = window.confirm(
      'Are you sure you want to submit your exam? You cannot change your answers after submission.'
    );

    if (!confirmSubmit) return;

    try {
      const results = await submitExam(exam.id, answers);
      setExamResults(results);
    } catch (err) {
      alert('Failed to submit exam: ' + err.message);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderQuestionInput = (question, questionIndex) => {
    const questionType = question.question_type;

    if (questionType === 'multiple_choice') {
      return (
        <div style={{ marginTop: '20px' }}>
          {question.options.map((option, optionIdx) => (
            <div key={optionIdx} style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                border: '2px solid',
                borderColor: answers[questionIndex] === option ? '#D4AF37' : '#ddd',
                borderRadius: '8px',
                background: answers[questionIndex] === option ? '#fff9e6' : '#fff',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}>
                <input
                  type="radio"
                  name={`question-${questionIndex}`}
                  value={option}
                  checked={answers[questionIndex] === option}
                  onChange={(e) => handleAnswerChange(questionIndex, e.target.value)}
                  style={{ marginRight: '12px', width: '20px', height: '20px' }}
                />
                <span style={{ fontSize: '16px' }}>{option}</span>
              </label>
            </div>
          ))}
        </div>
      );
    } else if (questionType === 'true_false') {
      return (
        <div style={{ marginTop: '20px', display: 'flex', gap: '15px' }}>
          {['True', 'False'].map((option) => (
            <label
              key={option}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '15px',
                border: '2px solid',
                borderColor: answers[questionIndex] === option ? '#D4AF37' : '#ddd',
                borderRadius: '8px',
                background: answers[questionIndex] === option ? '#fff9e6' : '#fff',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <input
                type="radio"
                name={`question-${questionIndex}`}
                value={option}
                checked={answers[questionIndex] === option}
                onChange={(e) => handleAnswerChange(questionIndex, e.target.value)}
                style={{ marginRight: '10px', width: '20px', height: '20px' }}
              />
              <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{option}</span>
            </label>
          ))}
        </div>
      );
    } else if (questionType === 'fill_in_blank') {
      return (
        <div style={{ marginTop: '20px' }}>
          <input
            type="text"
            value={answers[questionIndex] || ''}
            onChange={(e) => handleAnswerChange(questionIndex, e.target.value)}
            placeholder="Type your answer here..."
            style={{
              width: '100%',
              padding: '15px',
              fontSize: '16px',
              border: '2px solid #ddd',
              borderRadius: '8px',
              fontFamily: 'inherit'
            }}
          />
        </div>
      );
    }

    return null;
  };

  // Show results screen
  if (examResults) {
    const passed = examResults.passed;
    const scorePercentage = Math.round(examResults.score_percentage || 0);

    return (
      <div className="container">
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '40px',
          textAlign: 'center'
        }}>
          <div style={{
            padding: '40px',
            borderRadius: '12px',
            background: passed ? 'linear-gradient(135deg, #d4edda, #c3e6cb)' : 'linear-gradient(135deg, #f8d7da, #f5c6cb)',
            border: `3px solid ${passed ? '#28a745' : '#dc3545'}`,
            marginBottom: '30px'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>
              {passed ? '' : ''}
            </div>
            <h1 style={{
              margin: '0 0 15px 0',
              color: passed ? '#155724' : '#721c24',
              fontSize: '32px'
            }}>
              {passed ? 'Congratulations!' : 'Keep Learning'}
            </h1>
            <p style={{
              fontSize: '20px',
              margin: '0 0 25px 0',
              color: passed ? '#155724' : '#721c24'
            }}>
              {passed
                ? 'You have successfully passed the exam!'
                : 'You did not pass this time. Review the materials and try again.'
              }
            </p>
            <div style={{
              fontSize: '48px',
              fontWeight: 'bold',
              color: passed ? '#28a745' : '#dc3545',
              marginBottom: '10px'
            }}>
              {scorePercentage}%
            </div>
            <div style={{ fontSize: '16px', color: passed ? '#155724' : '#721c24' }}>
              Pass mark: {examResults.pass_mark || 70}%
            </div>
          </div>

          <div style={{
            padding: '30px',
            background: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '30px',
            textAlign: 'left'
          }}>
            <h3 style={{ marginTop: 0 }}>Exam Summary</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '15px' }}>
              <div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Correct Answers</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                  {examResults.correct_answers || 0} / {examResults.total_questions || 0}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Time Taken</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {examResults.time_taken_minutes || 'N/A'} min
                </div>
              </div>
            </div>
            <div style={{ fontSize: '14px', color: '#666', marginTop: '20px' }}>
              <strong>Submitted:</strong> {new Date(examResults.submitted_at).toLocaleString()}
            </div>
          </div>

          {passed ? (
            <div style={{ marginBottom: '20px' }}>
              <h3>Next Steps</h3>
              <p style={{ marginBottom: '20px' }}>
                Proceed to submit your evidence portfolio for assessor review.
              </p>
              <Link
                to={`/competency/evidence/${enrollmentId}`}
                style={{
                  display: 'inline-block',
                  padding: '15px 30px',
                  background: '#D4AF37',
                  color: '#fff',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  fontSize: '18px'
                }}
              >
                Submit Evidence
              </Link>
            </div>
          ) : (
            <div style={{ marginBottom: '20px' }}>
              <p style={{ marginBottom: '20px' }}>
                Review the course materials and try the exam again when you're ready.
              </p>
              <Link
                to={`/competency/learn/${enrollmentId}`}
                style={{
                  display: 'inline-block',
                  padding: '15px 30px',
                  background: '#2196f3',
                  color: '#fff',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  fontSize: '18px'
                }}
              >
                Review Course
              </Link>
            </div>
          )}

          <Link to="/competency/my-courses" style={{ display: 'inline-block', marginTop: '20px', color: '#D4AF37' }}>
            ← Back to My Courses
          </Link>
        </div>
      </div>
    );
  }

  // Show exam start screen
  if (!examStarted) {
    return (
      <div className="container">
        <Link to="/competency/my-courses" style={{ display: 'inline-block', marginBottom: '20px', color: '#D4AF37' }}>
          ← Back to My Courses
        </Link>

        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <div style={{
            padding: '40px',
            background: 'linear-gradient(135deg, #D4AF37, #B8860B)',
            color: '#fff',
            borderRadius: '12px',
            textAlign: 'center',
            marginBottom: '30px'
          }}>
            <h1 style={{ margin: '0 0 15px 0' }}>Professional Competency Exam</h1>
            <p style={{ fontSize: '18px', margin: 0, opacity: 0.95 }}>
              You are about to start your final examination
            </p>
          </div>

          <div style={{
            padding: '30px',
            background: '#fff',
            border: '2px solid #D4AF37',
            borderRadius: '8px',
            marginBottom: '30px'
          }}>
            <h3 style={{ marginTop: 0 }}>Exam Instructions</h3>
            <ul style={{ lineHeight: '2', marginBottom: '25px' }}>
              <li>The exam consists of scenario-based questions</li>
              <li>You must complete the exam within the time limit</li>
              <li>Each question carries equal weight</li>
              <li>You cannot pause or save progress</li>
              <li>You cannot go back after submitting</li>
              <li>Pass mark: 70%</li>
            </ul>

            <div style={{
              padding: '20px',
              background: '#fff9e6',
              border: '1px solid #D4AF37',
              borderRadius: '4px',
              marginBottom: '25px'
            }}>
              <h4 style={{ margin: '0 0 10px 0' }}>Exam Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <strong>Duration:</strong> 90 minutes
                </div>
                <div>
                  <strong>Questions:</strong> 30-60
                </div>
                <div>
                  <strong>Question Types:</strong> Multiple Choice, True/False, Fill-in-blank
                </div>
                <div>
                  <strong>Pass Mark:</strong> 70%
                </div>
              </div>
            </div>

            <div style={{
              padding: '15px',
              background: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: '4px',
              marginBottom: '25px',
              fontSize: '14px'
            }}>
              <strong> Important:</strong> Make sure you have a stable internet connection and enough time to complete the exam.
              The timer will start when you click "Start Exam".
            </div>

            <button
              onClick={handleStartExam}
              style={{
                width: '100%',
                padding: '18px',
                background: '#D4AF37',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '20px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Start Exam
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show exam interface
  if (examStarted && exam) {
    const questions = exam.selected_questions || [];
    const currentQuestion = questions[currentQuestionIndex];
    const answeredCount = Object.values(answers).filter(a => a !== '').length;
    const allAnswered = answeredCount === questions.length;

    return (
      <div className="container">
        {/* Exam Header with Timer */}
        <div style={{
          position: 'sticky',
          top: 0,
          background: '#fff',
          borderBottom: '2px solid #D4AF37',
          padding: '20px 0',
          marginBottom: '30px',
          zIndex: 100
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0 }}>Professional Competency Exam</h2>
              <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                Question {currentQuestionIndex + 1} of {questions.length}
              </div>
            </div>
            <div style={{
              padding: '15px 25px',
              background: timeRemaining < 300 ? '#ffebee' : '#f8f9fa',
              border: `2px solid ${timeRemaining < 300 ? '#f44336' : '#ddd'}`,
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Time Remaining</div>
              <div style={{
                fontSize: '28px',
                fontWeight: 'bold',
                fontFamily: 'monospace',
                color: timeRemaining < 300 ? '#f44336' : '#333'
              }}>
                {formatTime(timeRemaining)}
              </div>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Progress Bar */}
          <div style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Progress</span>
              <span style={{ fontSize: '14px', color: '#666' }}>
                {answeredCount} / {questions.length} answered
              </span>
            </div>
            <div style={{
              height: '8px',
              background: '#e0e0e0',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                background: 'linear-gradient(90deg, #D4AF37, #B8860B)',
                width: `${(answeredCount / questions.length) * 100}%`,
                transition: 'width 0.3s'
              }} />
            </div>
          </div>

          {/* Question Card */}
          {currentQuestion && (
            <div style={{
              padding: '40px',
              background: '#fff',
              border: '2px solid #ddd',
              borderRadius: '12px',
              marginBottom: '30px'
            }}>
              <div style={{ marginBottom: '10px' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '6px 12px',
                  background: '#e3f2fd',
                  color: '#2196f3',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase'
                }}>
                  {currentQuestion.question_type.replace('_', ' ')}
                </span>
              </div>

              <h3 style={{ fontSize: '20px', lineHeight: '1.6', marginBottom: '10px' }}>
                {currentQuestion.question_text}
              </h3>

              {currentQuestion.context && (
                <div style={{
                  padding: '15px',
                  background: '#f8f9fa',
                  borderLeft: '4px solid #D4AF37',
                  borderRadius: '4px',
                  marginBottom: '20px',
                  fontSize: '15px',
                  lineHeight: '1.6'
                }}>
                  {currentQuestion.context}
                </div>
              )}

              {renderQuestionInput(currentQuestion, currentQuestionIndex)}
            </div>
          )}

          {/* Navigation */}
          <div style={{ display: 'flex', gap: '15px', marginBottom: '40px' }}>
            <button
              onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
              disabled={currentQuestionIndex === 0}
              style={{
                padding: '12px 24px',
                background: currentQuestionIndex === 0 ? '#ddd' : '#fff',
                color: currentQuestionIndex === 0 ? '#999' : '#333',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontWeight: 'bold',
                cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              ← Previous
            </button>

            {currentQuestionIndex < questions.length - 1 ? (
              <button
                onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: '#D4AF37',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Next →
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: allAnswered ? '#28a745' : '#ffc107',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1
                }}
              >
                {submitting ? 'Submitting...' : allAnswered ? 'Submit Exam' : 'Submit (Some Unanswered)'}
              </button>
            )}
          </div>

          {/* Question Navigator */}
          <div style={{
            padding: '25px',
            background: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '40px'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '15px' }}>Question Navigator</h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))',
              gap: '10px'
            }}>
              {questions.map((q, idx) => {
                const isAnswered = answers[idx] !== '';
                const isCurrent = idx === currentQuestionIndex;

                return (
                  <button
                    key={idx}
                    onClick={() => setCurrentQuestionIndex(idx)}
                    style={{
                      padding: '12px',
                      background: isCurrent ? '#D4AF37' : isAnswered ? '#28a745' : '#fff',
                      color: isCurrent || isAnswered ? '#fff' : '#333',
                      border: isCurrent ? '2px solid #B8860B' : '1px solid #ddd',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CompetencyExam;
